import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Crown, Search, MessageCircle, User } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { SwipeableChat } from "@/components/SwipeableChat";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Heart } from "lucide-react";
import { apiClient } from "@/api/ApiClient";
import { BoyfriendProfile, ApiChatsResponse } from "@/api/models";

// Avatar maps removed - using URLs from server

// Use BoyfriendProfile from API models
type Contact = BoyfriendProfile;

interface ChatListProps {
  user: any;
  favorites: Set<string>;
  onToggleFavorite: (boyfriendId: string) => Promise<void>;
  onNavigateToChat: (boyfriendId: string) => void;
  onNavigateToPremium: () => void;
  onNavigateToExplore: () => void;
  onPromptLogin: () => void;
  onBottomNavVisibilityChange?: (visible: boolean) => void;
}

export const ChatList = ({
  user,
  favorites,
  onToggleFavorite,
  onNavigateToChat,
  onNavigateToPremium,
  onNavigateToExplore,
  onPromptLogin,
  onBottomNavVisibilityChange
}: ChatListProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [userStyle, setUserStyle] = useState<'anime' | 'realistic'>('realistic');
  const [swipedChatId, setSwipedChatId] = useState<string | null>(null);
  const [localFavorites, setLocalFavorites] = useState<Set<string>>(favorites);
  const [chatContacts, setChatContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Helper function to remove command from message text
  const removeCommandFromText = (text: string): string => {
    if (!text) return text;
    const commandMatch = text.match(/^#\[([A-Z_]+)\]#\s*/);
    if (commandMatch) {
      return text.substring(commandMatch[0].length);
    }
    return text;
  };

  // Load chats from API
  const loadChats = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      const data = await apiClient.get<ApiChatsResponse>('/chats');
      console.log('ChatList - API response:', data);
      if (data && data.items) {
        const contacts = data.items.map(item => {
          const rawText = item.lastMessage?.text || item.contact.bio || "Hey there!";
          return {
            ...item.contact,
            lastMessageText: removeCommandFromText(rawText),
            lastMessageTime: item.lastMessage?.createdAt || new Date().toISOString(),
            unreadCount: item.unreadCount || 0
          };
        });
        console.log('ChatList - contacts:', contacts.map(c => ({ id: c.id, name: c.name })));
        setChatContacts(contacts);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
      setApiError('Failed to load chats');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadChats();
  }, []);

  // Set bottom nav visible on mount
  useEffect(() => {
    if (onBottomNavVisibilityChange) {
      onBottomNavVisibilityChange(true);
    }
  }, [onBottomNavVisibilityChange]);

  const handleToggleFavoriteLocal = (contactId: string) => {
    setLocalFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(contactId)) {
        newFavorites.delete(contactId);
      } else {
        newFavorites.add(contactId);
      }
      return newFavorites;
    });
    
    onToggleFavorite(contactId);
  };

  const handleDeleteConversation = (contactId: string) => {
    // For now, just reset swipe state
    setSwipedChatId(null);
  };

  const isLoggedIn = !!user;

  if (!isLoggedIn) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">Sign in to start chatting</h2>
            <p className="text-muted-foreground mb-6">
              Connect with your AI boyfriends and enjoy personalized conversations
            </p>
            <Button variant="romantic" onClick={onPromptLogin}>
              <User className="h-4 w-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold mb-4">Loading chats...</h2>
            <p className="text-muted-foreground">
              Please wait while we load your conversations
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="bg-gradient-dreamy min-h-screen pb-16">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center max-w-md">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-4">Error loading chats</h2>
            <p className="text-muted-foreground mb-6">{apiError}</p>
            <Button variant="romantic" onClick={loadChats}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-dreamy min-h-screen pb-16">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-sm border-b border-primary/20 p-4 shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">{t('chat.messages')}</h1>
          <div className="flex items-center gap-2">
            {!isMobile && (
              <Button variant="ghost" size="icon">
                <Search className="h-5 w-5" />
              </Button>
            )}
            {!user?.isPremium && (
              <Button variant="romantic" size="sm" onClick={onNavigateToPremium}>
                <Crown className="h-4 w-4 mr-2" />
                Upgrade
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Contacts List */}
      <div className="flex-1 overflow-y-auto">
        {chatContacts.length === 0 ? (
          // No conversations message
          <div className="flex flex-col items-center justify-center h-full text-center px-4 py-16">
            <MessageCircle className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold text-muted-foreground mb-2">
              {t('chat_list.no_conversations')}
            </h3>
            <p className="text-sm text-muted-foreground/70 max-w-sm mb-6">
              {t('chat_list.start_chatting')}
            </p>
            <Button variant="romantic" onClick={onNavigateToExplore}>
              <MessageCircle className="h-4 w-4 mr-2" />
              {t('chat_list.explore_boyfriends')}
            </Button>
          </div>
        ) : isMobile ? (
          // Mobile: Use SwipeableChat component
          chatContacts.map((contact) => (
            <SwipeableChat
              key={contact.id}
              contact={contact}
              isSelected={swipedChatId === contact.id}
              onSelect={() => onNavigateToChat(contact.id)}
              onSwipe={(id) => setSwipedChatId(id)}
              onDelete={handleDeleteConversation}
              localFavorites={localFavorites}
              userStyle={userStyle}
            />
          ))
        ) : (
          // Desktop: Simple list
          chatContacts.map((contact) => (
            <div
              key={contact.id}
              className="p-4 border-b border-primary/10 hover:bg-card/50 cursor-pointer transition-colors bg-card/60 backdrop-blur-sm"
              onClick={() => onNavigateToChat(contact.id)}
            >
              <div className="flex items-start gap-3">
                <div className="relative">
                  <Avatar className="h-12 w-12 border-2 border-primary/20">
                    <AvatarImage 
                      src={contact.avatarUrl} 
                      alt={contact.name} 
                    />
                    <AvatarFallback>{contact.name[0]}</AvatarFallback>
                  </Avatar>
                  {/* Always show online indicator */}
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      <h3 className="font-semibold truncate">{contact.name}</h3>
                      {favorites.has(contact.id) && (
                        <Heart className="h-4 w-4 text-red-500 fill-red-500" />
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">Now</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground truncate flex-1 mr-2">
                      {(() => {
                        const text = (contact as any).lastMessageText || contact.bio || "Hey there!";
                        return text.length > 20 ? text.substring(0, 20) + "..." : text;
                      })()}
                    </p>
                    {(contact as any).unreadCount > 0 && (
                      <Badge variant="destructive" className="text-xs min-w-[20px] h-5 flex items-center justify-center">
                        1
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};